import isEmpty from 'lodash/isEmpty';
import round from 'lodash/round';
import { v4 } from 'uuid';

import { DAILY, MONTHLY, WEEKLY } from '../Constants/Signal';
import { SCAN_TYPE_SET } from '../Constants/SignalCollection';
import { CONNECT_DISCONNECT_ID } from '../Constants/StaticUUIDs';
import {
  AREA,
  BAR_ENGINE_UTILIZATION,
  BAR_FUEL_UTILIZATION,
  BOOLEAN,
  CAN_SCAN,
  COLUMN_GRAPH,
  COMMAND,
  GAUGE,
  GAUGE_RANGE,
  HIGH_BAR,
  IMAGE,
  LINE_GRAPH,
  LINE_HIGH_CHART,
  LOCATION,
  MAINTENANCE,
  MAP,
  PIE,
  TEXT,
  TEXT_ENGINE,
  TEXT_FUEL,
  TEXT_STATIC,
  TEXT_TAGS,
  VERTICAL_COLUMN_GRAPH,
} from '../Constants/WidgetTypes';

export const ENGINE_HOURS_HELP_TEXT =
  'Note: Engine Hours Utilization Tile requires Engine Hours "reporting_type" to be configured on Asset Model Signals';
export const FUEL_USED_HELP_TEXT =
  'Note: Fuel Used Utilization Tile requires Fuel Used "reporting_type" to be configured on Asset Model Signals';
export const GAUGE_HELP_TEXT =
  'Configure 1 or 2 gauges for display on this smart tile';
export const IMAGE_HELP_TEXT =
  'Click on the image below to add up to 10 signals for this smart tile';
export const MAINTENANCE_HELP_TEXT =
  'Select Maintenance Reminders to display in this smart tile';
export const MAP_HELP_TEXT =
  "All map widgets are configured automatically, there's no need to select any collections or signals";
export const SCAN_SIGNAL_TEXT = 'Choose either a CAN or J1939 Scan signal';
export const TEN_SIGNAL_TEXT =
  'You can add up to 10 signals for this smart tile';
export const TEXT_STATIC_HELP_TEXT =
  'Add up to 512 characters of freeform text to be displayed on the dashboard';

export default class WidgetUtils {
  constructor() {
    this.types = [
      {
        helpText: TEN_SIGNAL_TEXT,
        label: 'Area Line Chart',
        maxSignals: 10,
        value: AREA,
      },
      {
        helpText: ENGINE_HOURS_HELP_TEXT,
        label: 'Bar Chart - Engine Hour Utilization',
        value: BAR_ENGINE_UTILIZATION,
      },
      {
        helpText: FUEL_USED_HELP_TEXT,
        label: 'Bar Chart - Fuel Used Utilization',
        value: BAR_FUEL_UTILIZATION,
      },
      {
        helpText: TEN_SIGNAL_TEXT,
        label: 'Bar Chart - Horizontal',
        maxSignals: 10,
        value: COLUMN_GRAPH,
      },
      {
        helpText: TEN_SIGNAL_TEXT,
        label: 'Bar Chart - Vertical',
        maxSignals: 10,
        value: VERTICAL_COLUMN_GRAPH,
      },
      { label: 'Boolean - Latest Data Available', value: BOOLEAN },
      {
        helpText: SCAN_SIGNAL_TEXT,
        label: 'CAN/J1939 Scan',
        maxSignals: 1,
        value: CAN_SCAN,
      },
      { label: 'Command and Control', value: COMMAND },
      {
        helpText: MAP_HELP_TEXT,
        label: 'Current Asset Location',
        maxSignals: 2,
        value: MAP,
      },
      {
        helpText: GAUGE_HELP_TEXT,
        label: 'Gauge - Most Recent Value',
        maxSignals: 2,
        value: GAUGE,
      },
      {
        helpText: GAUGE_HELP_TEXT,
        label: 'Gauge - Flexible Range Values',
        maxSignals: 2,
        value: GAUGE_RANGE,
      },
      {
        helpText: IMAGE_HELP_TEXT,
        label: 'Image Overlay',
        maxSignals: 10,
        value: IMAGE,
      },
      {
        helpText: TEN_SIGNAL_TEXT,
        label: 'Line Graph',
        maxSignals: 10,
        value: LINE_GRAPH,
      },
      {
        helpText: MAINTENANCE_HELP_TEXT,
        label: 'Maintenance',
        value: MAINTENANCE,
      },
      {
        helpText: TEN_SIGNAL_TEXT,
        label: 'Pie Chart',
        maxSignals: 10,
        value: PIE,
      },
      {
        helpText: MAP_HELP_TEXT,
        label: 'Recent Asset Path',
        maxSignals: 2,
        value: LOCATION,
      },
      { label: 'Text', value: TEXT },
      {
        helpText: TEXT_STATIC_HELP_TEXT,
        label: 'Text (Static)',
        value: TEXT_STATIC,
      },
      { label: 'Text (Tags)', value: TEXT_TAGS },
      {
        helpText: ENGINE_HOURS_HELP_TEXT,
        label: 'Text - Engine Hour Utilization',
        value: TEXT_ENGINE,
      },
      {
        helpText: FUEL_USED_HELP_TEXT,
        label: 'Text - Fuel Used Utilization',
        value: TEXT_FUEL,
      },
    ];

    this.legacyTypes = [
      {
        helpText: TEN_SIGNAL_TEXT,
        label: 'Line Graph',
        maxSignals: 10,
        value: LINE_HIGH_CHART,
      },
      {
        helpText: TEN_SIGNAL_TEXT,
        label: 'Bar Chart',
        maxSignals: 10,
        value: HIGH_BAR,
      },
    ];

    this.settingsSignalArrayNameMap = new Map([
      [AREA, 'lines'],
      [BOOLEAN, BOOLEAN],
      [COLUMN_GRAPH, 'columns'],
      [COMMAND, 'commands'],
      [HIGH_BAR, 'columns'],
      [IMAGE, 'lines'],
      [LINE_GRAPH, 'lines'],
      [LINE_HIGH_CHART, 'lines'],
      [PIE, 'lines'],
      [CAN_SCAN, CAN_SCAN],
      [TEXT, TEXT],
      [VERTICAL_COLUMN_GRAPH, 'columns'],
    ]);

    this.timeAggregationOptions = [
      { label: 'Daily Aggregation', value: DAILY },
      { label: 'Weekly Aggregation', value: WEEKLY },
      { label: 'Monthly Aggregation', value: MONTHLY },
    ];

    this.widgetSignalDefault = {
      datapoint: {},
      maxValue: '',
      minValue: '',
      ranges: [{ id: v4() }],
      signal: {},
    };
    this.nonSignalTypes = new Set([
      BAR_ENGINE_UTILIZATION,
      BAR_FUEL_UTILIZATION,
      COMMAND,
      MAINTENANCE,
      TEXT_ENGINE,
      TEXT_FUEL,
      TEXT_STATIC,
      TEXT_TAGS,
    ]);
  }

  createDatapointMapForDisplay(datapoints = [], scanSelected) {
    const eventCollectionOptions = datapoints
      .map((dp) => {
        const { _id: datapointID, name: datapointName, type, values } = dp;
        if (scanSelected && !SCAN_TYPE_SET.has(type)) return;

        return {
          value: datapointID,
          label: datapointName,
          values: values
            .map(({ _id, name, label }) => {
              return { value: _id, label: name || label };
            })
            .filter((s) => !!s),
        };
      })
      .filter((dp) => !!dp);
    const outputSignalOption = {
      label: 'Calculated Signal',
      value: 'outputSignal',
    };
    return [outputSignalOption, ...eventCollectionOptions];
  }

  parseFetchedData(fetchedData, lines, last) {
    if (!lines.length) return new Map();
    return lines.reduce((map, { datapointID }) => {
      if (fetchedData.has(datapointID)) {
        const data = fetchedData.get(datapointID);
        const targetData = last ? data[0] || {} : data;
        map.set(datapointID, targetData);
      }
      return map;
    }, new Map());
  }

  parseEventsForLatest(lines, dataMap, max = null) {
    const signals = Array.from(dataMap.values()).reduce(
      (acc, { signals } = {}) => {
        if (Object.keys(signals || {})?.length) {
          acc.push(...Object.values(signals));
        }
        return acc;
      },
      []
    );

    return lines.reduce((series, { key }, i) => {
      const { value } = signals.find(({ name }) => name === key) || {};
      if (max) lines[i].originalValue = value;

      series =
        value || [0, '0'].includes(value)
          ? [...series, { name: key, data: [value] }]
          : [...series, { name: key, data: [] }];

      return series;
    }, []);
  }

  parseEventsForPieChart(lines, dataMap) {
    return lines.reduce((series, { datapointID, key }) => {
      const { signals } = dataMap.get(datapointID) ?? {};
      if (!Object.keys(signals || {})?.length) return series;

      const { value } =
        Object.values(signals).find(({ name }) => name === key) || {};
      if (!value && value !== 0) return series;
      return series.concat(value);
    }, []);
  }

  parseEventsForTimeseries(lines, dataMap, signalNamesMap, signalUnitsMap) {
    const series = lines.reduce((s, { key: name, datapointID, type }) => {
      return s.set(name, { name, data: [], datapointID, signalType: type });
    }, new Map());

    dataMap.forEach((value, key) => {
      if (isEmpty(value)) return;
      for (const event of value) {
        const { signals, createdAt } = event;
        const time = new Date(createdAt).getTime();
        Object.entries(signals || {}).forEach(([signalID, signal]) => {
          const { name, value: val } = signal || {};
          const signalName = signalNamesMap.get(signalID) ?? name;
          const signalInSeries = series.get(signalName);

          if (signalInSeries) {
            const { datapointID } = signalInSeries ?? {};
            if (datapointID === key) {
              const { display_precision = 2 } =
                signalUnitsMap.get(datapointID)?.[signalName] ?? {};
              const dataPoint = [time, round(val, display_precision)];
              series.set(signalName, {
                ...signalInSeries,
                data: [...signalInSeries.data, dataPoint],
              });
            }
          }
        });
      }
    });

    return Array.from(series.values());
  }

  parseEventsForGraph(
    lines = [{}],
    dataDict,
    type,
    signalNamesMap,
    signalUnitsMap
  ) {
    switch (type) {
      case AREA:
      case LINE_HIGH_CHART:
      case LINE_GRAPH:
        return this.parseEventsForTimeseries(
          lines,
          dataDict,
          signalNamesMap,
          signalUnitsMap
        );
      case COLUMN_GRAPH:
      case HIGH_BAR:
      case IMAGE:
      case VERTICAL_COLUMN_GRAPH:
        return this.parseEventsForLatest(lines, dataDict);
      case PIE:
        return this.parseEventsForPieChart(lines, dataDict);
    }
  }

  getWidgetAttributes(type, widgetSettings) {
    const { columns, title } = widgetSettings;
    let { lines } = widgetSettings;
    let last = false;

    switch (type) {
      case COLUMN_GRAPH:
      case HIGH_BAR:
      case IMAGE:
      case VERTICAL_COLUMN_GRAPH:
        last = true;
        if (columns) lines = columns;
        break;
      case PIE:
        last = true;
        break;
    }
    return { last, lines, title };
  }

  noDataIsAvailable(series = [], type) {
    const locationWidgetKeys = new Set(['latitude', 'longitude']);
    switch (type) {
      case AREA:
      case COLUMN_GRAPH:
      case HIGH_BAR:
      case IMAGE:
      case LINE_HIGH_CHART:
      case LINE_GRAPH:
      case VERTICAL_COLUMN_GRAPH:
        return series?.every?.(({ data }) => !data?.length);
      case BOOLEAN:
      case TEXT:
        return series?.every?.(({ value }) => !value && value !== 0);
      case GAUGE:
      case GAUGE_RANGE:
      case MAINTENANCE:
        return false;
      case MAP: {
        const { latitude, longitude } = series.reduce((acc, { signals }) => {
          if (!Object.keys(signals || {})?.length) return acc;
          Object.values(signals).forEach(({ name, value }) => {
            if (locationWidgetKeys.has(name)) acc[name] = value;
          });
          return acc;
        }, {});
        return !latitude || !longitude;
      }
      case COMMAND:
      case LOCATION:
      case PIE:
      case CAN_SCAN:
      case TEXT_TAGS:
        return !series?.length;
      case BAR_ENGINE_UTILIZATION:
      case BAR_FUEL_UTILIZATION:
      case TEXT_ENGINE:
      case TEXT_FUEL:
        return !series?.size;
      case TEXT_STATIC:
        return !series?.links?.length && !series?.staticText?.length;
      default:
        return true;
    }
  }

  buildRealtimeSubscriptionKeySet(gatewayID, { settings = {}, type } = {}) {
    const result = new Set();

    switch (true) {
      case 'lines' in settings:
        settings?.lines?.forEach?.(({ datapointID }) => {
          result.add(`${gatewayID}:${datapointID}`);
        });
        break;
      case 'columns' in settings:
        settings?.columns?.forEach?.(({ datapointID }) => {
          result.add(`${gatewayID}:${datapointID}`);
        });
        break;
      case TEXT in settings:
        settings?.text?.forEach?.(({ datapointID }) => {
          result.add(`${gatewayID}:${datapointID}`);
        });
        break;
      case BOOLEAN in settings:
        settings?.boolean?.forEach?.(({ datapointID }) => {
          result.add(`${gatewayID}:${datapointID}`);
        });
        break;
      case CAN_SCAN in settings:
        settings?.scan?.forEach?.(({ datapointID }) => {
          result.add(`${gatewayID}:${datapointID}`);
        });
        break;
      case type === LOCATION:
      case type === MAP:
        result
          .add(`${gatewayID}:${CONNECT_DISCONNECT_ID}`)
          .add(`${gatewayID}:${settings?.datapointID}`);
        break;
      case 'datapointID' in settings: {
        result.add(`${gatewayID}:${settings.datapointID}`);
        if (settings.secondGauge?.datapointID) {
          result.add(`${gatewayID}:${settings.secondGauge.datapointID}`);
        }

        break;
      }
    }

    return result;
  }
}
